import { lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router';
import { useAuth } from '../data/AuthContext';
import { AppLoading } from './AppLoading';
import { LoginPage } from './LoginPage';
import { NoAccess } from './NoAccess';

// Loaded lazily so the marketing site's chunk isn't in the initial
// bundle for the (far more common) signed-in app session.
const MarketingHomePage = lazy(() =>
  import('./marketing/MarketingSitePage').then(m => ({ default: m.MarketingHomePage })));

export function AuthGate() {
  const { isLoggedIn, isLoading, hasSession, noProfile } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // A visible holding screen — never a blank page — while the session
    // check runs (AuthContext caps how long this state can last).
    return <AppLoading />;
  }

  if (!isLoggedIn) {
    // Signed in with Supabase but not on the team roster (e.g. an
    // unrecognized Google account) — show the provisioning screen
    // instead of bouncing back to the login form.
    if (hasSession && noProfile) {
      return <NoAccess />;
    }
    // The site root is the marketing homepage for visitors who aren't
    // signed in (and aren't mid-sign-in). Protected deep links — e.g. a
    // shared /deliverables/123 — still get the login form in place so
    // sign-in lands them exactly where they were headed.
    if (location.pathname === '/' && !hasSession) {
      return (
        <Suspense fallback={<AppLoading />}>
          <MarketingHomePage />
        </Suspense>
      );
    }
    return <LoginPage />;
  }

  return <Outlet />;
}
